// src/services/authService.js
import { endpoints } from '../api/endpoints';

export const loginUser = async (email, password, rememberMe = false) => {
    const response = await fetch(endpoints.Login, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
    });

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            throw new Error('Login failed: ' + response.statusText);
        }
        throw new Error(errorData.message || 'Invalid credentials');
    }

    const data = await response.json();
    return data;  // Returns { accessToken, refreshToken, email, role, expiresIn }
};

export const registerUser = async (userData) => {
    const response = await fetch(endpoints.Register, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
    }

    return await response.json();
};

export const refreshToken = async (refreshToken) => {
    const response = await fetch(endpoints.RefreshToken, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
        throw new Error('Token refresh failed');
    }

    return await response.json();
};

export const getCurrentUser = async (token) => {
    const response = await fetch(endpoints.GetCurrentUser, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to get current user');
    }

    return await response.json();
};

export const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    const apiClient = (await import('./apiClient')).default;
    apiClient.clearTokens();

    if (refreshToken) {
        try {
            await fetch(endpoints.Logout, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });
        } catch (e) {
            // Best-effort: the local session is already cleared regardless of network failure.
        }
    }
};

export const verifyEmail = async (token) => {
    try {
        console.log('[Auth Service] Verifying email with token:', token);
        const url = `${endpoints.VerifyEmail}?token=${token}`;
        console.log('[Auth Service] Request URL:', url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        console.log('[Auth Service] Response status:', response.status);
        console.log('[Auth Service] Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json();
            console.error('[Auth Service] Error response:', errorData);
            throw new Error(errorData.message || 'Email verification failed');
        }

        const data = await response.json();
        console.log('[Auth Service] Success response:', data);
        return data;
    } catch (error) {
        console.error('[Auth Service] Verification error:', error);
        throw error;
    }
};

export const resendVerificationEmail = async (email) => {
    const response = await fetch(endpoints.ResendVerification, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to resend verification email');
    }

    return await response.json();
};

export const getUserProfile = async () => {
    const apiClient = (await import('./apiClient')).default;
    const response = await apiClient.request('/auth/profile', {
        method: 'GET',
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get user profile');
    }

    return await response.json();
};

export const updateProfile = async (profileData) => {
    const apiClient = (await import('./apiClient')).default;
    const response = await apiClient.request('/auth/profile', {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
    }

    return await response.json();
};

export const changePassword = async (currentPassword, newPassword) => {
    const apiClient = (await import('./apiClient')).default;
    const response = await apiClient.request('/auth/change-password', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to change password');
    }

    return await response.json();
};

export const deleteAccount = async () => {
    const apiClient = (await import('./apiClient')).default;
    const response = await apiClient.request('/auth/profile', {
        method: 'DELETE',
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete account');
    }

    return await response.json();
};
